<template>
  <component
    :is="tagName"
    v-bind="tagProps"
    :class="`${classes} ${!$slots.default && icon ? 'is-only-icon' : ''}`"
    :disabled="buttonDisabled"
    :style="`--btn-color: ${color}`"
    @click="handleClick"
    @keydown="$emit('keydown', $event)"
    @mousedown="$emit('mousedown', $event)"
    @mouseleave="$emit('mouseleave', $event)"
    @onmousemove="$emit('onmousemove', $event)">
    <slot name="content">
      <div class="s-btn-content">
        <sIcon
          v-if="icon || loading"
          :class="`${loading ? '' : iconClass} s-btn-icon`"
          :line-height="iconLineHeight"
          :size="iconSizeValue"
          :spin="loading"
          :type="loading ? 'loading' : icon"/>
        <span
          v-if="$slots.default"
          class="s-btn-label">
          <slot/>
        </span>
      </div>
    </slot>
  </component>
</template>
<script>
import sIcon from './icon'

const btn = 's-btn'
/**
 * Компонент "Кнопка"
 * @displayName Button
 */
export default {
  name: 'sButton',
  componentName: 'sButton',
  inject: {
    elForm: {
      default: ''
    },
  },
  methods: {
    handleClick(event) {
      document.activeElement.blur()
      if (this.disabled || this.loading) { return }
      this.$emit('click', event)
    },
    handleCheckClick(event, newWindow = false) {
      if (this.to) {
        if (this.target === '_blank') {
          return
        }
        event.preventDefault()
        this.handleClick(newWindow)
      }
    },
  },
  computed: {
    classes() {
      const classes = [btn, `${btn}-${this.type}`]
      if (this.buttonDisabled) { classes.push('is-disabled') }
      if (this.icon) { classes.push('is-icon') }
      if (this.loading) { classes.push('is-loading') }
      if (this.rounded) { classes.push('is-rounded') }
      if (this.small) { classes.push('is-small') }
      if (this.tagName === 'router-link') { classes.push('is-inline-block') }
      return classes.join(' ')
    },
    /**
     * Розмір іконки числом: sIcon рахує ширину як `${size}px`, тож `'22px'` дало б невалідне `22pxpx`
     */
    iconSizeValue() {
      return this.iconSize ? parseFloat(this.iconSize) : 16
    },
    /**
     * line-height іконки. sIcon сприймає line-height без `px` як множник розміру (size × lineHeight),
     * тож розмір, переданий числом (`22` / `'22'`), доповнюємо `px`
     */
    iconLineHeight() {
      if (!this.iconSize) {
        return '16px'
      }
      return /^\d+(\.\d+)?$/.test(String(this.iconSize)) ? `${this.iconSize}px` : this.iconSize
    },
    buttonDisabled() {
      return this.disabled || (this.elForm || {}).disabled
    },
    tagName() {
      if (this.href) {
        return 'a'
      } else if (this.to) {
        return 'router-link'
      }
      return 'button'
    },
    tagProps() {
      if (this.tagName === 'a') {
        if (this.disabled || this.loading) {
          return {}
        }
        const { target } = this
        return { href: this.href, target }
      } else if (this.tagName === 'router-link') {
        return { to: this.to }
      }
      return {}
    },
  },
  components: {
    sIcon
  },
  props: {
    /**
     * Тип кнопки
     */
    type: {
      type: String,
      validator: function (value) {
        const correct = ['black', 'green', 'border', 'text'].indexOf(value) !== -1
        return correct
      },
      default: 'black',
    },
    /**
     * Цвет фона для типа `black` или текста для 'transparent' и 'text'
     */
    color: {
      type: String,
      default: 'var(--text-color)'
    },
    /**
     * отключение
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * статус загрузки
     */
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * иконка для отображения
     */
    icon: {
      type: String,
      default: '',
    },
    /**
     * Стиль для иконки
     */
    iconClass: {
      type: String,
      default: ''
    },
    /**
     * Размер иконки
     */
    iconSize: {
      type: [String, Number]
    },
    /**
     * Закругленная кнопка
     */
    rounded: {
      type: Boolean,
      default: true
    },
    /**
     * Ссылка. Тег будет `a`
     */
    href: {
      type: String,
      default: '',
    },
    /**
     * Адрес роута. Тег будет `router-link`
     */
    to: {
      type: Object,
    },
    /**
     * Маленькая кнопка (уменьшаем отступы и высоту)
     */
    small: {
      type: Boolean,
      default: false
    }
  },
}
</script>
